import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The tool row: the small things a shopper can do with a product that are not
 * buying it — put it beside another, look at what it used to cost, keep it for
 * later.
 *
 *   <ProductPanelTools>
 *     <ProductPanelTool onClick={compare} aria-pressed={isInComparison}>
 *       <IconScale className="size-6" />
 *       <ProductPanelToolLabel variant={ProductPanelToolVariant.DEFAULT}>
 *         Porovnat
 *       </ProductPanelToolLabel>
 *     </ProductPanelTool>
 *
 *     <DialogTrigger render={<ProductPanelTool />}>
 *       <History className="size-5.5" />
 *       <ProductPanelToolLabel variant={ProductPanelToolVariant.DEFAULT}>
 *         Historie cen
 *       </ProductPanelToolLabel>
 *     </DialogTrigger>
 *   </ProductPanelTools>
 *
 * **Nothing here knows whether a product is in a comparison set or among a
 * shopper's favourites.** Those are atom reads a tenant owns; a tool takes the
 * copy it shows and, where it toggles something, says so with `aria-pressed`
 * through the native prop spread. The mark beside the copy is composed in too —
 * icon sets are a tenant's until they are a platform's.
 *
 * Server-first: every part here is a plain component. Each tool is the TRIGGER
 * of something interactive — a popover, a dialog — and the interactive piece is
 * the primitive the tenant wraps it in, which is why a tool is a plain
 * `<button>` that a `DialogTrigger` or a `PopoverAnchor` can render through.
 */

/**
 * The row.
 *
 * Wraps rather than scrolls: the tools are independent of one another, so a
 * panel too narrow for three across drops one to a second line instead of
 * hiding it. The gap between columns is wide and the gap between rows is not —
 * three affordances on one line read as three, and the same three stacked read
 * as a list.
 */
const productPanelToolsClasses = '@container flex flex-wrap gap-x-10 gap-y-4';

export type ProductPanelToolsProps = ComponentProps<'div'>;

/** The row of tools. */
export const ProductPanelTools = ({ className, ...props }: ProductPanelToolsProps) => (
  <div
    data-slot="product-panel-tools"
    className={clsxm(productPanelToolsClasses, className)}
    {...props}
  />
);

/**
 * One tool: a mark and a word, together a single target.
 *
 * A real `<button>` rather than a styled box with a role bolted on. Every tool
 * in this row opens or toggles something, so the element that does it is the
 * one browsers already give a focus ring, a keyboard activation and a role —
 * and a primitive asked to render through this one inherits all three instead
 * of re-deriving them.
 *
 * `group` is load-bearing: the mark and the copy both answer to a pointer over
 * the whole target, not over themselves. It is the row's own hover contract, so
 * it sits in the recipe rather than at every call site.
 *
 * No appearance axis. What differs between a tool that adds and a tool that
 * removes is the COLOUR its copy and its mark take on hover, and each of those
 * carries the axis itself — the target's own box is one shape throughout.
 */
const productPanelToolBoxClasses =
  'group flex w-max cursor-pointer items-center gap-x-3 transition';

export type ProductPanelToolProps = ComponentProps<'button'> & {
  /**
   * The element to paint instead of the `<button>`. A tool that navigates
   * rather than opens or toggles, such as a sign-in link for an anonymous
   * shopper, renders through an `<a>` and keeps the row's box and hover.
   */
  readonly render?: RenderElement;
};

/**
 * One tool in the row.
 *
 * `type="button"` by default and overridable. A tool inside a form must not
 * submit it, so the default is the safe direction. A call site that wants a
 * submit says so. A tool painted through `render` keeps an explicit `type` and
 * gets no default. The element it names can be an `<a>`, where `type` means
 * something else. A `render={<button />}` names its own `type`.
 */
export const ProductPanelTool = ({ className, type, render, ...props }: ProductPanelToolProps) =>
  renderThrough(
    render,
    'product-panel-tool',
    clsxm(productPanelToolBoxClasses, className),
    type === undefined ? props : { ...props, type }
  ) ?? (
    <button
      data-slot="product-panel-tool"
      type={type ?? 'button'}
      className={clsxm(productPanelToolBoxClasses, className)}
      {...props}
    />
  );

/**
 * What a tool's copy does under a pointer.
 *
 * Muted at rest in both appearances, because a tool row sits beneath the thing
 * a shopper came to do and must not compete with it. What the hover promises is
 * the difference: `default` firms up to the page's own ink — this will happen —
 * while `destructive` goes to the warning colour, for the tool that undoes
 * something rather than doing it.
 *
 * Exported for a tenant painting its own mark to the same rule; the mark itself
 * is composed in, and its colouring is not this package's to spell.
 */
export const productPanelToolClasses = {
  default: 'text-foreground-muted group-hover:text-foreground',
  destructive: 'text-foreground-muted group-hover:text-destructive',
  unstyled: '',
} as const;

type ProductPanelToolVariantName = keyof typeof productPanelToolClasses;

export type ProductPanelToolVariant = ProductPanelToolVariantName;

export const ProductPanelToolVariant = {
  /** Doing the thing. */
  DEFAULT: 'default',
  /** Undoing it. */
  DESTRUCTIVE: 'destructive',
  /** The opt-out: a tenant colouring the tool itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductPanelToolVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductPanelToolVariantProps = { readonly variant: ProductPanelToolVariant };

export const productPanelToolLabelVariants = cva('', {
  variants: { variant: productPanelToolClasses },
});

/**
 * What a tool's MARK does under a pointer — the same rule as its copy, because
 * the two answer to the same target and must not disagree.
 *
 * A recipe rather than a part: which drawing a tool carries and how big it
 * reads are the call site's (a scale, a clock and a heart are not one size),
 * while what colour it takes at rest and on hover is the row's.
 */
export const productPanelToolMarkVariants = cva('', {
  variants: { variant: productPanelToolClasses },
});

export type ProductPanelToolLabelProps = ComponentProps<'span'> & ProductPanelToolVariantProps;

/** A tool's word. */
export const ProductPanelToolLabel = ({
  className,
  variant,
  ...props
}: ProductPanelToolLabelProps) => (
  <span
    data-slot="product-panel-tool-label"
    className={clsxm(productPanelToolLabelVariants({ variant }), className)}
    {...props}
  />
);
